import { useCallback, useEffect, useState } from 'react';
import {
  getRecruiterSMTPAccounts,
  ISMTPAccountsResponse,
  getJobOwnersWhoBlockedRecruiter,
  validateEmails,
  ICandidate,
  ITemplateResponse,
  IJob,
  IBulkEmail,
  replacePlaceHolders,
  getCandidateResumeByFileUrl,
  fetchCandidateResponse,
  getRtrPdfs,
  IBulkEmailAttachment,
} from 'wisestep-api';

export const useFetchRecruiterSMTPAccounts = () => {
  const [fromEmailList, setFromEmailList] = useState<{
    data: ISMTPAccountsResponse[];
    isLoaded: boolean;
  }>({
    data: [],
    isLoaded: false,
  });

  const getFromAccounts = useCallback(async () => {
    const response = await getRecruiterSMTPAccounts();
    setFromEmailList({
      data: response.data,
      isLoaded: true,
    });
  }, []);

  useEffect(() => {
    getFromAccounts();
  }, [getFromAccounts]);
  return fromEmailList;
};

export const fetchJobOwnersWhoBlockedRecruiter = (recruiterId: string) => {
  let jobOwners: string[] = [];
  const getJobOwners = async () => {
    const response = await getJobOwnersWhoBlockedRecruiter(recruiterId);
    jobOwners = response.data;
  };

  getJobOwners();

  return jobOwners;
};

export const validateAllEmails = async (
  toEmails: string[],
  ccEmails?: string[],
  bccEmails?: string[]
): Promise<[invalidToEmails: string[], invalidCcEmails: string[], invalidBccEmails: string[]]> => {
  try {
    const response = await validateEmails(toEmails, ccEmails ?? [], bccEmails ?? []);
    const data = response.data;
    return new Promise<
      [invalidToEmails: string[], invalidCcEmails: string[], invalidBccEmails: string[]]
    >((resolve) => {
      resolve([data.invalidToEmails, data.invalidBCCEmails, data.invalidCCEmails]);
    });
  } catch (error) {
    return new Promise<
      [invalidToEmails: string[], invalidCcEmails: string[], invalidBccEmails: string[]]
    >((resolve) => {
      resolve([[], [], []]);
    });
  }
};

export const replacePlaceHoldersEmailBody = async (
  template: ITemplateResponse,
  candidate: ICandidate,
  job: IJob | null,
  skip = true,
  submissionEmailBody?: string
): Promise<{
  updatedBody: string;
  updatedSubject: string;
  updatedAttachments: IBulkEmailAttachment[];
}> => {
  const bulkEmailData: IBulkEmail = {
    interactions: [candidate],
    jobId: job?.id || '',
    job,
    subject: template.subject || '',
    body: template.body || '',
    from: '',
    to: [''],
    title: '',
    cc: template?.cc || [],
    bcc: template?.bcc || [],
  };

  const { data: response } = await replacePlaceHolders(bulkEmailData);
  let updatedBody = submissionEmailBody || response?.body || '';
  let updatedAttachments: IBulkEmailAttachment[] = [];

  if (skip && updatedBody.includes('{{candidateSummary}}') && candidate) {
    const candidateSummary = await getCandidateSummary(candidate, job);
    updatedBody = updatedBody.replaceAll('{{candidateSummary}}', candidateSummary);
  }
  if (
    updatedBody.includes('{{rtr}}') ||
    updatedBody.includes('{{candidateCv}}') ||
    (updatedBody.includes('{{candidate.otherDocuments}}') && candidate && skip)
  ) {
    const { updatedTemplateBody, attachments } = await replacePlaceholdersWithAttachments(
      updatedBody,
      candidate
    );
    (updatedBody = updatedTemplateBody), (updatedAttachments = attachments);
  }
  return { updatedBody, updatedSubject: response?.subject || '', updatedAttachments };
};

const getCandidateSummary = async (candidate: ICandidate, job: IJob | null): Promise<string> => {
  const candidateResumeUrl = candidate.resumeLocation || '';
  const resumeResponse = await getCandidateResumeByFileUrl(candidateResumeUrl);
  const resumeContent = resumeResponse.data?.content || '';

  const { data: summary } = await fetchCandidateResponse({
    resumeContent,
    jobDescription: job?.description || '',
  });

  return summary.replace(/^- /gm, '<br/> -> ') || '';
};

export const replacePlaceholdersWithAttachments = async (
  templateBody: string,
  candidate: ICandidate
): Promise<{ updatedTemplateBody: string; attachments: IBulkEmailAttachment[] }> => {
  const attachments: IBulkEmailAttachment[] = [];

  if (candidate && templateBody.includes('{{rtr}}')) {
    templateBody = templateBody.replaceAll('{{rtr}}', '');
    const rtrResponse = await getRtrPdfs(candidate?.candidateId ?? candidate?.id ?? '');
    if (rtrResponse?.data?.documentLocation)
      attachments.push({
        attachementName: rtrResponse.data.originalName || 'RtrResponseDoc',
        attachementURL: rtrResponse.data.documentLocation,
      });
  }

  if (candidate && templateBody.includes('{{candidateCv}}')) {
    templateBody = templateBody.replaceAll('{{candidateCv}}', '');
    if (candidate?.resumeLocation)
      attachments.push({
        attachementName: candidate?.resumeFileOriginalName || 'CandidateCV',
        attachementURL: candidate?.resumeLocation,
      });
  }
  if (templateBody.includes('{{candidate.otherDocuments}}') && candidate) {
    templateBody = templateBody.replaceAll('{{candidate.otherDocuments}}', '');
    const other_documents: IBulkEmailAttachment[] =
      candidate?.otherDocuments?.map((document) => ({
        attachementName: document?.originalName || 'other_documenet',
        attachementURL: document?.documentLocation || '',
      })) ?? [];
    attachments.push(...other_documents);
  }

  return { updatedTemplateBody: templateBody, attachments };
};
